// Shared truckload-split helpers — used by the Hero estimator, the Services
// hub, the Product page, and anywhere else we add multi-load orders to the
// cart. Centralizing keeps the 5yd-per-load minimum billing and the multi-
// delivery line-item split consistent across every entry point.
export function isLightMaterial(name: string): boolean {
  const n = name.toLowerCase()
  return (
    n.includes('bark') ||
    n.includes('compost') ||
    n.includes('topsoil') ||
    n.includes('50/50') ||
    n.includes('fill dirt') ||
    n.includes('play chips')
  )
}
export function getFullLoad(name: string): number {
  return isLightMaterial(name) ? 15 : 12
}
// Minimum billable yards per delivery — a partial last load below this is
// rounded UP to MIN_LOAD so customers aren't surprised by a tiny load that
// still requires a full truck and driver.
export const MIN_LOAD = 5
/**
 * Split a total yardage into per-delivery quantities.
 *
 * Rules:
 * - Loads 1..N-1 are always fullLoad yards.
 * - The last load is the remainder, but billed up to MIN_LOAD if smaller.
 * - For totals ≤ fullLoad, returns a single load equal to max(total, MIN_LOAD).
 *
 * Returns an array of yards per delivery. e.g. for non-light material:
 *   splitIntoDeliveries(16) → [12, 5]   // 4yd remainder → billed as 5
 *   splitIntoDeliveries(20) → [12, 8]   // 8yd remainder kept as-is
 *   splitIntoDeliveries(9)  → [9]
 *   splitIntoDeliveries(3)  → [5]       // single small load, billed at min
 */
export function splitIntoDeliveries(
  totalYards: number,
  productName: string,
): number[] {
  const fullLoad = getFullLoad(productName)
  const total = Math.max(0, Math.round(totalYards))
  if (total <= 0) return []
  if (total <= fullLoad) {
    return [Math.max(MIN_LOAD, total)]
  }
  const deliveries = Math.ceil(total / fullLoad)
  const lastLoadActual = total - (deliveries - 1) * fullLoad
  const lastLoadBillable = Math.max(MIN_LOAD, lastLoadActual)
  const loads: number[] = []
  for (let i = 0; i < deliveries - 1; i++) loads.push(fullLoad)
  loads.push(lastLoadBillable)
  return loads
}
/**
 * Convenience: turn a split result into cart-line-item descriptors so callers
 * can iterate and addToCart() consistently. The product name gets a
 * "(Delivery N)" suffix when there's more than one load, matching the cart
 * drawer's per-delivery breakdown rendering.
 *
 * When the last delivery's partial load was rounded UP to the 5-yard
 * minimum, the returned object includes `requestedQuantity` representing
 * the actual material yards needed (e.g. 4 yd on a 16yd → 12 + 5 split).
 * Callers should forward this onto the CartItem so the drawer can explain
 * that the customer isn't getting extra material — just billed at the min.
 */
export function buildDeliveryLineItems(
  totalYards: number,
  productName: string,
): { product: string; quantity: number; requestedQuantity?: number }[] {
  const fullLoad = getFullLoad(productName)
  const total = Math.max(0, Math.round(totalYards))
  const loads = splitIntoDeliveries(totalYards, productName)
  if (loads.length === 0) return []
  if (loads.length === 1) {
    // Single-load: only flag a requestedQuantity if the customer asked for
    // less than the 5-yard minimum (we bumped it up).
    const billed = loads[0]
    const item: {
      product: string
      quantity: number
      requestedQuantity?: number
    } = {
      product: productName,
      quantity: billed,
    }
    if (total > 0 && total < billed) item.requestedQuantity = total
    return [item]
  }
  // Multi-delivery: compute the actual material needed on the last load
  // before the 5yd-min bump. If it's already ≥ MIN_LOAD, no flag needed.
  const deliveries = loads.length
  const lastLoadActual = total - (deliveries - 1) * fullLoad
  return loads.map((qty, i) => {
    const isLast = i === deliveries - 1
    const item: {
      product: string
      quantity: number
      requestedQuantity?: number
    } = {
      product: `${productName} (Delivery ${i + 1})`,
      quantity: qty,
    }
    if (isLast && lastLoadActual > 0 && lastLoadActual < qty) {
      item.requestedQuantity = lastLoadActual
    }
    return item
  })
}
